import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { VOICE_INPUT_STATE, VoiceInputService, VoiceInputState } from '@services/voice-input-service';

@Component({
  selector: 'voice-mic-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './voice-mic-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoiceMicInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showTooltip: boolean = false;
  @Input() tooltipDelayMs: number = 400;
  @Output() transcript = new EventEmitter<string>();
  @Output() stateChange = new EventEmitter<VoiceInputState>();
  @Output() tooltipShown = new EventEmitter<void>();

  private readonly voiceInput = inject(VoiceInputService);
  private readonly cdr = inject(ChangeDetectorRef);

  state: VoiceInputState = VOICE_INPUT_STATE.IDLE;
  tooltipReady: boolean = false;

  get isListening(): boolean {
    return this.state === VOICE_INPUT_STATE.LISTENING;
  }
  private stateSubscription?: Subscription;
  private listenSubscription?: Subscription;
  private tooltipTimer?: ReturnType<typeof setTimeout>;
  private tooltipImpressionLogged: boolean = false;

  ngOnInit(): void {
    this.stateSubscription = this.voiceInput.state$.subscribe((next: VoiceInputState): void => {
      this.state = next;
      this.stateChange.emit(next);
      this.cdr.markForCheck();
      this.maybeLogTooltipImpression();
    });
    this.tooltipTimer = setTimeout((): void => {
      this.tooltipReady = true;
      this.cdr.markForCheck();
      this.maybeLogTooltipImpression();
    }, this.tooltipDelayMs);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showTooltip) {
      this.maybeLogTooltipImpression();
    }
  }

  private maybeLogTooltipImpression(): void {
    if (this.tooltipImpressionLogged) return;
    if (!this.showTooltip || !this.tooltipReady || this.state === VOICE_INPUT_STATE.LISTENING) return;
    this.tooltipImpressionLogged = true;
    this.voiceInput.notifyTooltipShown();
    this.tooltipShown.emit();
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.listenSubscription?.unsubscribe();
    if (this.tooltipTimer) clearTimeout(this.tooltipTimer);
  }

  onClick(): void {
    if (this.state === VOICE_INPUT_STATE.LISTENING) return;
    this.listenSubscription?.unsubscribe();
    this.listenSubscription = this.voiceInput.listen().subscribe({
      next: (text: string): void => {
        if (text) this.transcript.emit(text);
      },
      error: (): void => {
        // service already logged + reset state; nothing to do here
      },
    });
  }
}
